window.BAO.utils ??= {}

/**
 * Returns the first element that is a descendant of node that
 * matches selectors.
 *
 * @param {String} selector - A DOMString containing one or more selectors to match. This string
 *   must be a valid CSS selector string
 * @param {Object} [config] - You can pass a context to scope the element selection
 * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element in
 *
 * @returns {HTMLElement} The found element or null
 */
function getElement (selector, { context = document } = {}) {
  return context.querySelector(selector)
}

/**
 * Returns a list of elements that are a descendant of node that
 * matches selectors.
 *
 * @param {String} selector - A DOMString containing one or more selectors to match. This string
 *   must be a valid CSS selector string
 * @param {Object} [config] - You can pass a context to scope the element selection
 * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element in
 * @param {Boolean} [asArray=true] - Return the elements as an Array instead of an NodeList?
 *
 * @returns {Array|NodeList} The found elements or null
 */
function getElements (selector, { context = document, asArray = true } = {}) {
  const items = context.querySelectorAll(selector)

  return asArray ? Array.from(items) : items
}

/**
 * Checks if the passed string contains any items in an array
 *
 * @param {String} str - The string we will be searching
 * @param {Array} [items] - A list of the items we will be checking for presence in the str
 *
 * @returns {Boolean}
 */
function doesStringContainAny (str, items = []) {
  return items.some(i => str.includes(i))
}

/**
 * Creates an HTMLElement
 *
 * @param {String} tag - What type of element we want this to be
 * @param {Object} [options] - A key / value pair of properties and values we want to set on the
 *   newly created element
 *
 * @returns {HTMLElement}
 */
function createElement (tag, options = {}) {
  const el = document.createElement(tag)

  Object.entries(options).forEach(([key, value]) => {
    el.setAttribute(key, value)
  })

  return el
}

/**
 * Creates an array starting from zero with the length of the passed amount
 *
 * @param {Number} amount - How long do we want this array to be?
 *
 * @returns {Array}
 */
function range (amount) {
  return [...Array(amount).keys()]
}

/**
 * camelCase a string
 *
 * @param {String} str
 *
 * @return {String} value - formatted value
 */
function camelize (str) {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase()
    })
    .replace(/\s+/g, '')
}

/**
 * A Javascript equivalent of Shopify's | img_url filter
 *
 * @param {Object} The properties we will use to create the URL
 * @param {String} src - The Shopify CDN URL of the image
 * @param {String} size - What size we want the image to be
 * @param {null|String} [crop=null] - By default the images aren't cropped but we can choose to
 *   crop them
 * @param {null|Number} [scale=null] - Images are 1x by default, you can choose to pass a different
 *   scale
 *
 * @returns {String}
 */
function resizeImage ({ src, size, crop = null, scale = null } = {}) {
  if (size == null) {
    return src
  }

  if (size === 'master') {
    return src.replace(/http(s)?:/, '')
  }

  const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?/i)

  if (match != null) {
    const prefix = src.split(match[0])
    const suffix = match[0]
    const scaleString = scale ? `@${scale}x` : ''
    const cropString = crop ? `_crop_${crop}` : ''

    return `${prefix[0]}_${size}${cropString}${scaleString}${suffix}`.replace(
      /http(s)?:/,
      '',
    )
  } else {
    return null
  }
}

/**
 * This is the default config we will use when using fetch()
 *
 * @returns {Object}
 */
function getDefaultRequestConfig () {
  return {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  }
}

/**
 * Default fetch boilerplate
 * @param {String} url - The URL we are looking to fetch
 * @param {Object} [config] - .fetch config to override the default
 *
 * @returns {Promise}
 */
function fetchJSON (url, config = {}) {
  return fetch(url, Object.assign({}, getDefaultRequestConfig(), config)).then(
    response => {
      if (!response.ok) {
        throw response
      }
      return response.json()
    },
  )
}

/**
 * This will fetch product data from Shopify's AJAX API by handle
 *
 * @param {String} handle - The products handle
 *
 * @returns {Promise}
 */
function fetchProductData (handle) {
  try {
    return fetchJSON(`/products/${handle}.js`)
  } catch (e) {
    console.error(e)
  }
}

/**
 * Gets the offsetTop of the passed element relevant to it's offsetParent
 *
 * @param {HTMLElement|Element} el - The element we'll be getting the offsetTop for
 * @param {Document|HTMLElement} [parent=HTMLBodyElement] - The offsetParent we want to get the offsetTop relative to
 * @param {Number} [offsetTop=0] - The starting offsetTop. This tends to be 0 but given this is a
 *   recursive function, we need to pass it to let it build up
 *
 * @returns {Number}
 */
function getOffsetTop (el, parent = document.body, offsetTop = 0) {
  if (!isNaN(el.offsetTop)) {
    offsetTop += el.offsetTop
  }

  return el.offsetParent === parent
    ? offsetTop
    : getOffsetTop(el.offsetParent, parent, offsetTop)
}

/**
 * Accepts a date in the format of YYYY-MM-DD
 *
 * @returns {Boolean}
 */

function checkDateIsInTheFuture (dateToTest) {
  var currentDate = new Date();
  // Format the date as "YYYY-MM-DD"
  var formattedDate = currentDate.toISOString().split('T')[0];

  // Convert date strings to Date objects
  var date1 = new Date(formattedDate);
  var date2 = new Date(dateToTest);

  if (date2 > date1) {
    return true
  } else {
    return false
  }
}

function generateId(prefix = '') {
  if (crypto?.randomUUID) {
    return `${prefix}${crypto.randomUUID()}`;
  }

  // Fallback
  return (
    prefix +
    Math.random().toString(36).substring(2) +
    Date.now().toString(36)
  );
}

const CUSTOMER_KEY_STORAGE = 'elevate_customer_key';
const SESSION_KEY_STORAGE = 'elevate_session_key';
const CUSTOMER_KEY_MAX_AGE = 34560000; // 400 days

function getCookie(name) {
  try {
    const encodedName = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(encodedName)) {
        return decodeURIComponent(trimmed.slice(encodedName.length));
      }
    }
  } catch (e) {
    // ignore
  }

  return null;
}

function setCookie(name, value, { maxAge } = {}) {
  try {
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; SameSite=Lax`;

    if (maxAge != null) {
      cookie += `; Max-Age=${maxAge}`;
    }

    if (location.protocol === 'https:') {
      cookie += '; Secure';
    }

    document.cookie = cookie;
  } catch (e) {
    // ignore quota / private mode
  }
}

function deleteCookie(name) {
  try {
    let cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax`;

    if (location.protocol === 'https:') {
      cookie += '; Secure';
    }

    document.cookie = cookie;
  } catch (e) {
    // ignore
  }
}

function clearTrackingIdentityStorage() {
  deleteCookie(CUSTOMER_KEY_STORAGE);

  try {
    localStorage.removeItem(CUSTOMER_KEY_STORAGE);
  } catch (e) {
    // ignore quota / private mode
  }

  try {
    sessionStorage.removeItem(SESSION_KEY_STORAGE);
  } catch (e) {
    // ignore quota / private mode
  }
}

/**
 * Reads the Elevate customer key from its cookie, migrating a legacy localStorage value if present.
 * @returns {string | null}
 */
function retrieveElevateKey() {
  const cookieKey = getCookie(CUSTOMER_KEY_STORAGE);

  if (cookieKey) {
    return cookieKey;
  }

  try {
    const legacyKey = localStorage.getItem(CUSTOMER_KEY_STORAGE);

    if (!legacyKey) {
      return null;
    }

    setCookie(CUSTOMER_KEY_STORAGE, legacyKey, { maxAge: CUSTOMER_KEY_MAX_AGE });
    localStorage.removeItem(CUSTOMER_KEY_STORAGE);

    return legacyKey;
  } catch (e) {
    return null;
  }
}

/** @returns {'granted' | 'denied' | 'unknown'} */
function getTrackingConsentState() {
  try {
    const getter = window.Shopify?.customerPrivacy?.getTrackingConsent;
    if (typeof getter !== 'function') {
      return 'unknown';
    }
    const consent = getter();
    if (consent === true || consent === 'yes') {
      return 'granted';
    }
    if (consent === false || consent === 'no') {
      return 'denied';
    }
    return 'unknown';
  } catch (e) {
    return 'unknown';
  }
}

function getCustomerKey() {
  const consentState = getTrackingConsentState();

  if (consentState === 'denied') {
    clearTrackingIdentityStorage();
    return generateId('anon_cust_');
  }

  if (consentState === 'unknown') {
    const storedKey = retrieveElevateKey();
    if (storedKey) {
      return storedKey;
    }
    return generateId('anon_cust_');
  }

  if (window.BAO.customer_id != null) {
    setCookie(CUSTOMER_KEY_STORAGE, window.BAO.customer_id, { maxAge: CUSTOMER_KEY_MAX_AGE });
  }

  let customerKey = retrieveElevateKey();

  if (!customerKey) {
    customerKey = generateId('cust_');
    setCookie(CUSTOMER_KEY_STORAGE, customerKey, { maxAge: CUSTOMER_KEY_MAX_AGE });
  }

  return customerKey;
}

function getSessionKey() {
  const consentState = getTrackingConsentState();

  if (consentState === 'denied') {
    clearTrackingIdentityStorage();
    return generateId('anon_sess_');
  }

  if (consentState === 'unknown') {
    const storedKey = sessionStorage.getItem(SESSION_KEY_STORAGE);
    if (storedKey) {
      return storedKey;
    }
    return generateId('anon_sess_');
  }

  let sessionKey = sessionStorage.getItem(SESSION_KEY_STORAGE);

  if (!sessionKey) {
    sessionKey = generateId('sess_');
    sessionStorage.setItem(SESSION_KEY_STORAGE, sessionKey);
  }

  return sessionKey;
}

// country groupings
const AUSNZ = ["AU", "NZ"];

const EUR = [
  "AL", // Albania
  "AM", // Armenia
  "AZ", // Azerbaijan
  "BA", // Bosnia and Herzegovina
  "BG", // Bulgaria
  "LI", // Liechtenstein
  "CH", // Switzerland
  "CZ", // Czechia
  "DK", // Denmark
  "AD", // Andorra
  "AT", // Austria
  "BE", // Belgium
  "CY", // Cyprus
  "EE", // Estonia
  "ES", // Spain
  "FR", // France
  "GF", // French Guiana
  "GR", // Greece
  "GP", // Guadeloupe
  "IT", // Italy
  "HR", // Croatia
  "IE", // Ireland
  "LT", // Lithuania
  "LV", // Latvia
  "LU", // Luxembourg
  "MC", // Monaco
  "ME", // Montenegro
  "MQ", // Martinique
  "MT", // Malta
  "NL", // Netherlands
  "PT", // Portugal
  "RE", // Réunion
  "SI", // Slovenia
  "SK", // Slovakia
  "SM", // San Marino
  "VA", // Holy See (Vatican City State)
  "YT", // Mayotte
  "FI", // Finland
  "MF", // Saint Martin (French part)
  "BL", // Saint Barthélemy
  "GE", // Georgia
  "HU", // Hungary
  "IS", // Iceland
  "MD", // Moldova, Republic of
  "MK", // North Macedonia
  "NO", // Norway
  "PL", // Poland
  "RO", // Romania
  "RS", // Serbia
  "SE"  // Sweden
];

const DE = ["DE"];

const USCAN = ["CA", "US"];

const ROW = [
  "AE", // United Arab Emirates
  "SX", // Sint Maarten (Dutch part)
  "CW", // Curaçao
  "AO", // Angola
  "AR", // Argentina
  "KI", // Kiribati
  "NR", // Nauru
  "TV", // Tuvalu
  "AW", // Aruba
  "BB", // Barbados
  "BD", // Bangladesh
  "BH", // Bahrain
  "BI", // Burundi
  "BM", // Bermuda
  "BN", // Brunei Darussalam
  "BO", // Bolivia, Plurinational State of
  "BR", // Brazil
  "BS", // Bahamas
  "BT", // Bhutan
  "BW", // Botswana
  "BZ", // Belize
  "CL", // Chile
  "CN", // China
  "CO", // Colombia
  "CR", // Costa Rica
  "CV", // Cabo Verde
  "DJ", // Djibouti
  "FO", // Faroe Islands
  "GL", // Greenland
  "DO", // Dominican Republic
  "DZ", // Algeria
  "EG", // Egypt
  "ET", // Ethiopia
  "FJ", // Fiji
  "FK", // Falkland Islands (Malvinas)
  "GH", // Ghana
  "GI", // Gibraltar
  "GM", // Gambia
  "GN", // Guinea
  "GT", // Guatemala
  "GY", // Guyana
  "HK", // Hong Kong
  "HN", // Honduras
  "HT", // Haiti
  "ID", // Indonesia
  "IL", // Israel
  "IN", // India
  "JM", // Jamaica
  "JO", // Jordan
  "JP", // Japan
  "KE", // Kenya
  "KG", // Kyrgyzstan
  "KH", // Cambodia
  "KM", // Comoros
  "KR", // Korea, Republic of
  "KW", // Kuwait
  "KY", // Cayman Islands
  "KZ", // Kazakhstan
  "LA", // Lao People's Democratic Republic
  "LB", // Lebanon
  "LK", // Sri Lanka
  "LR", // Liberia
  "LS", // Lesotho
  "MA", // Morocco
  "MG", // Madagascar
  "MN", // Mongolia
  "MO", // Macao
  "MR", // Mauritania
  "MU", // Mauritius
  "MV", // Maldives
  "MW", // Malawi
  "MX", // Mexico
  "MY", // Malaysia
  "MZ", // Mozambique
  "NA", // Namibia
  "NG", // Nigeria
  "NI", // Nicaragua
  "NP", // Nepal
  "CK", // Cook Islands
  "NU", // Niue
  "OM", // Oman
  "PA", // Panama
  "PE", // Peru
  "PG", // Papua New Guinea
  "PH", // Philippines
  "PK", // Pakistan
  "PY", // Paraguay
  "QA", // Qatar
  "RW", // Rwanda
  "SA", // Saudi Arabia
  "SB", // Solomon Islands
  "SC", // Seychelles
  "SG", // Singapore
  "SH", // Saint Helena, Ascension and Tristan da Cunha
  "SL", // Sierra Leone
  "SR", // Suriname
  "ST", // Sao Tome and Principe
  "SZ", // Eswatini
  "TH", // Thailand
  "TM", // Turkmenistan
  "TN", // Tunisia
  "TO", // Tonga
  "TT", // Trinidad and Tobago
  "TW", // Taiwan, Province of China
  "TZ", // Tanzania, United Republic of
  "UG", // Uganda
  "EC", // Ecuador
  "SV", // El Salvador
  "TC", // Turks and Caicos Islands
  "TL", // Timor-Leste
  "VG", // Virgin Islands (British)
  "UY", // Uruguay
  "UZ", // Uzbekistan
  // Rest of the World → no ISO country code
  "VE", // Venezuela, Bolivarian Republic of
  "VN", // Viet Nam
  "VU", // Vanuatu
  "WS", // Samoa
  "CM", // Cameroon
  "CG", // Congo
  "GA", // Gabon
  "GQ", // Equatorial Guinea
  "TD", // Chad
  "AG", // Antigua and Barbuda
  "AI", // Anguilla
  "DM", // Dominica
  "GD", // Grenada
  "LC", // Saint Lucia
  "MS", // Montserrat
  "VC", // Saint Vincent and the Grenadines
  "KN", // Saint Kitts and Nevis
  "BF", // Burkina Faso
  "BJ", // Benin
  "CI", // Côte d'Ivoire
  "GW", // Guinea-Bissau
  "SN", // Senegal
  "TG", // Togo
  "PF", // French Polynesia
  "WF", // Wallis and Futuna
  "NC", // New Caledonia
  "ZA", // South Africa
  "ZM", // Zambia
  "ZW"  // Zimbabwe
];

const GB = ["GB", "GG", "IM", "JE"];

const countryGroups = {
  "AUSNZ": AUSNZ,
  "EUR": EUR,
  "USCAN": USCAN,
  "DE": DE,
  "GB": GB,
  "ROW": ROW
};

function getCountryGroup(countryCode) {
  for (const [groupName, array] of Object.entries(countryGroups)) {
    if (array.includes(countryCode)) {
      return groupName;
    }
  }
  return null; // not found in any group
}

Object.assign(window.BAO.utils, {
  camelize,
  createElement,
  doesStringContainAny,
  fetchJSON,
  fetchProductData,
  getDefaultRequestConfig,
  getElement,
  getElements,
  getOffsetTop,
  range,
  resizeImage,
  checkDateIsInTheFuture,
  generateId,
  getCookie,
  getCustomerKey,
  getSessionKey,
  getCountryGroup,
})
